import type { EpcProjectAttachment } from '../../../services/epcProjectService';

/* ── Document category tags ──────────────────────────────────────────────
 *  Retuned to read premium on cream — muted tints, ring-bordered chips.
 *  Categories stay DISTINCT (they carry meaning); labels/shorts unchanged.
 */
export const DOC_TAGS = [
  { label: 'Customer Agreement & Documents', short: 'Contract', color: 'bg-[rgba(31,90,71,0.08)]   text-[#1F5A47] ring-[rgba(31,90,71,0.28)]'   },
  { label: 'Minutes of Meeting',             short: 'MoM',      color: 'bg-[rgba(99,72,138,0.08)]   text-[#5B4581] ring-[rgba(99,72,138,0.26)]'   },
  { label: 'Customer Standard Drawing',      short: 'Drawing',  color: 'bg-[rgba(46,74,143,0.08)]   text-[#2E4A8F] ring-[rgba(46,74,143,0.26)]'   },
  { label: 'Material MQAP',                  short: 'MQAP',     color: 'bg-[rgba(201,169,97,0.16)]  text-[#8A5A08] ring-[rgba(201,169,97,0.42)]'  },
  { label: 'Work Completion Certificates',   short: 'WCC',      color: 'bg-[rgba(51,146,116,0.10)]  text-[#1F6B52] ring-[rgba(51,146,116,0.30)]'  },
  { label: 'Project Progress Report',        short: 'Report',   color: 'bg-[rgba(176,108,32,0.10)]  text-[#9A5B12] ring-[rgba(176,108,32,0.30)]'  },
  { label: 'Project SOPs',                   short: 'SOP',      color: 'bg-[rgba(31,90,71,0.10)]    text-[#1F5A47] ring-[rgba(31,90,71,0.30)]'    },
  { label: 'Project Land Documents',         short: 'Land',     color: 'bg-[rgba(70,113,58,0.10)]   text-[#3F6432] ring-[rgba(70,113,58,0.28)]'   },
  { label: 'Other',                          short: 'Other',    color: 'bg-[rgba(124,138,130,0.12)] text-[#5E6B63] ring-[rgba(124,138,130,0.32)]' },
] as const;

export function tagMeta(label: string) {
  return DOC_TAGS.find((t) => t.label === label) ?? null;
}

/* ── Filename encoding — all metadata lives inside fileName ──────────────
 *  Project file:    "[Tag Label] Display name"  or  "Display name"
 *  Dept file:       "[DEPT:orgId][Tag Label] Display name"  or  "[DEPT:orgId] Display name"
 */
export function buildFileName(name: string, tag: string, deptId?: string): string {
  const base = tag ? `[${tag}] ${name}` : name;
  return deptId ? `[DEPT:${deptId}]${base}` : base;
}

export function parseFileName(raw: string): { deptId: string | null; tag: string | null; displayName: string } {
  let s = raw;
  let deptId: string | null = null;
  const deptM = s.match(/^\[DEPT:([^\]]+)\]/);
  if (deptM) { deptId = deptM[1]; s = s.slice(deptM[0].length); }
  const tagM = s.match(/^\[([^\]]+)\] (.+)$/);
  if (tagM) return { deptId, tag: tagM[1], displayName: tagM[2] };
  return { deptId, tag: null, displayName: s };
}

export interface DictionaryDoc {
  fileKey: string;
  fileName: string;      // raw encoded name
  displayName: string;
  tag: string | null;
  deptId: string | null; // null = project-level
}

export function toDictionaryDocs(attachments: EpcProjectAttachment[]): DictionaryDoc[] {
  return attachments.map((a) => {
    const parsed = parseFileName(a.fileName);
    return { fileKey: a.fileKey, fileName: a.fileName, ...parsed };
  });
}
